import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { authApi } from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState({});
  const [moduleFlags, setModuleFlags] = useState({
    reservationsModuleEnabled: false,
    ordersModuleEnabled: false,
    paymentsModuleEnabled: false
  });

  const checkAuth = useCallback(async () => {
    try {
      const res = await authApi.getMe();
      setUser(res.data);
      setPermissions(res.data.permissions || {});
      setModuleFlags(res.data.moduleFlags || {
        reservationsModuleEnabled: false,
        ordersModuleEnabled: false,
        paymentsModuleEnabled: false
      });
    } catch {
      setUser(null);
      setPermissions({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (e) {
      // Logout API failure is non-blocking — session is cleared locally regardless
      console.warn('Logout API call failed:', e);
    }
    setUser(null);
    setPermissions({});
  };

  // Check if user has permission for a module
  const hasPermission = (module, action = 'view') => {
    if (!user) return false;
    if (user.role === 'ADMIN') return true;
    const perm = permissions[module];
    if (!perm) return false;
    return action === 'view' ? perm.can_view : perm.can_edit;
  };

  // Check if a module is enabled
  const isModuleEnabled = (module) => {
    const flagMap = {
      'RESERVATIONS': 'reservationsModuleEnabled',
      'ORDERS': 'ordersModuleEnabled',
      'PAYMENTS': 'paymentsModuleEnabled'
    };
    const flag = flagMap[module];
    if (!flag) return true; // Non-flagged modules are always enabled
    return moduleFlags[flag];
  };

  // Check if user can access a module (permission + module enabled)
  const canAccessModule = (module, action = 'view') => {
    if (['RESERVATIONS', 'ORDERS'].includes(module) && !isModuleEnabled(module)) {
      return false;
    }
    return hasPermission(module, action);
  };

  return (
    <AuthContext.Provider value={{ 
      user, setUser, loading, logout, checkAuth,
      permissions, setPermissions, moduleFlags, setModuleFlags,
      hasPermission, isModuleEnabled, canAccessModule
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
